import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, GripVertical, Link2 } from 'lucide-react';
import React, { useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import {
  AxisKey,
  AxisPreferences,
  CurveId,
  CurvePreferences,
  CurveRelation,
} from '../../config/plotTypes';
import { AxisMappingDropdown } from './AxisMappingDropdown';
import { ColorPicker } from './ColorPicker';
import { InterpolationToggleGroup } from './InterpolationToggleGroup';
import { LineStyleDropdown } from './LineStyleDropdown';
import { NameInput } from './NameInput';
import { ThicknessSlider } from './ThicknessSlider';
import { VisibilityToggle } from './VisibilityToggle';

export interface CurveSettingsProps {
  curves: Record<CurveId, CurvePreferences>;
  curveOrder?: CurveId[];
  relations?: CurveRelation[];
  plotType?: 'timeseries' | 'scatter';
  axes: Record<AxisKey, AxisPreferences>;
  dataSourceToAxis: Record<CurveId, AxisKey>;
  onUpdateCurve: (key: CurveId, prefs: CurvePreferences) => void;
  onUpdateAxisMapping: (dataSourceKey: CurveId, axisKey: AxisKey) => void;
  onReorderCurve?: (sourceIndex: number, destinationIndex: number) => void;
}

export const CurveSettings: React.FC<CurveSettingsProps> = ({
  curves,
  curveOrder,
  relations,
  plotType = 'timeseries',
  axes,
  dataSourceToAxis,
  onUpdateCurve,
  onUpdateAxisMapping,
  onReorderCurve,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination || !onReorderCurve) return;
    onReorderCurve(result.source.index, result.destination.index);
  };

  const fallbackOrder = Object.keys(curves) as CurveId[];
  const resolvedCurveOrder = [
    ...(curveOrder ?? []).filter((curveKey) => curveKey in curves),
    ...fallbackOrder.filter(
      (curveKey) => !(curveOrder ?? []).includes(curveKey)
    ),
  ];
  const curveEntries: Array<[CurveId, CurvePreferences]> =
    resolvedCurveOrder.map((curveKey) => [curveKey, curves[curveKey]]);

  const intervalGroupByCurve = new Map<CurveId, number>();
  const showStyleOptions = plotType === 'timeseries';
  const showPointSizeOption = plotType === 'scatter';
  const showColorOption = showStyleOptions || showPointSizeOption;
  (relations ?? []).forEach((relation, relationIndex) => {
    if (relation.relationType !== 'interval') return;
    const groupedCurveIds = [
      ...new Set([
        ...(relation.relatedCurves ?? []).slice(0, 2),
        relation.mainCurve,
      ]),
    ].filter((curveId): curveId is CurveId => Boolean(curves[curveId]));

    groupedCurveIds.forEach((curveId) => {
      intervalGroupByCurve.set(curveId, relationIndex);
    });
  });

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: '0.9rem', fontWeight: 700 }}
        mb={1}
      >
        Paramètres des courbes
      </Typography>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="curves">
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {curveEntries.map(([key, prefs], index) => {
                const isExpanded = expandedKeys.has(key);
                const mappedAxisKey = dataSourceToAxis[key];
                const isXAxisCurve =
                  mappedAxisKey !== undefined &&
                  Boolean(axes[mappedAxisKey]?.isXAxis);
                const nextCurveKey = curveEntries[index + 1]?.[0];
                const currentIntervalGroup = intervalGroupByCurve.get(key);
                const nextIntervalGroup = nextCurveKey
                  ? intervalGroupByCurve.get(nextCurveKey)
                  : undefined;
                const showLinkedIcon =
                  currentIntervalGroup !== undefined &&
                  currentIntervalGroup === nextIntervalGroup;

                return (
                  <React.Fragment key={key}>
                    <Draggable
                      draggableId={key}
                      index={index}
                      isDragDisabled={isXAxisCurve}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            mb: showLinkedIcon ? 0.25 : 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                          }}
                        >
                          {/* Header row */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            px={1}
                            py={0.75}
                          >
                            {/* Grab handle */}
                            <Box
                              sx={{
                                cursor: isXAxisCurve ? 'default' : 'grab',
                                color: 'text.disabled',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {!isXAxisCurve && (
                                <Box
                                  {...provided.dragHandleProps}
                                  aria-label="Reorder curve"
                                >
                                  <GripVertical size={16} />
                                </Box>
                              )}
                            </Box>
                            {/* Expand toggle - only this triggers collapse */}
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(key)}
                              sx={{
                                transition: 'transform 0.2s',
                                transform: isExpanded
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <ChevronDown size={16} />
                            </IconButton>
                            {/* Color swatch */}
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '4px',
                                backgroundColor: prefs.color || '#000',
                                border: '1px solid rgba(0,0,0,0.12)',
                                flexShrink: 0,
                              }}
                            />
                            {/* Name */}
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ flexGrow: 1, minWidth: 0 }}
                            >
                              {prefs.displayName || key}
                            </Typography>
                            {/* Visibility */}
                            <VisibilityToggle
                              visible={prefs.visible ?? true}
                              onToggle={() =>
                                onUpdateCurve(key, {
                                  ...prefs,
                                  visible: !prefs.visible,
                                })
                              }
                            />
                          </Stack>
                          {/* Collapsible details */}
                          <Collapse in={isExpanded}>
                            <Divider />
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap={1.25}
                              p={1.5}
                            >
                              <NameInput
                                value={prefs.displayName || ''}
                                onChange={(value) =>
                                  onUpdateCurve(key, {
                                    ...prefs,
                                    displayName: value,
                                  })
                                }
                              />
                              {showColorOption && (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    mb={0.5}
                                  >
                                    {showPointSizeOption
                                      ? 'Couleur des points'
                                      : 'Couleur'}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <ColorPicker
                                      value={prefs.color || '#000000'}
                                      onChange={(value) =>
                                        onUpdateCurve(key, {
                                          ...prefs,
                                          color: value,
                                        })
                                      }
                                    />
                                    <TextField
                                      value={prefs.color || '#000000'}
                                      onChange={(e) =>
                                        onUpdateCurve(key, {
                                          ...prefs,
                                          color: e.target.value,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                      placeholder="#000000"
                                    />
                                  </Stack>
                                </Box>
                              )}
                              {showStyleOptions && (
                                <>
                                  <Box>
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={0.5}
                                    >
                                      <Typography variant="caption">
                                        Epaisseur
                                      </Typography>
                                      <Typography variant="caption">
                                        {(prefs.thicknessPx || 1) + 'px'}
                                      </Typography>
                                    </Box>
                                    <ThicknessSlider
                                      value={prefs.thicknessPx || 1}
                                      min={1}
                                      max={10}
                                      onChange={(value) =>
                                        onUpdateCurve(key, {
                                          ...prefs,
                                          thicknessPx: value,
                                        })
                                      }
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      mb={0.5}
                                    >
                                      Style de ligne
                                    </Typography>
                                    <LineStyleDropdown
                                      value={prefs.lineStyle || 'solid'}
                                      onChange={(value) =>
                                        onUpdateCurve(key, {
                                          ...prefs,
                                          lineStyle: value,
                                        })
                                      }
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      mb={0.5}
                                    >
                                      Interpolation
                                    </Typography>
                                    <InterpolationToggleGroup
                                      value={prefs.shape || 'linear'}
                                      onChange={(value) =>
                                        onUpdateCurve(key, {
                                          ...prefs,
                                          shape: value,
                                        })
                                      }
                                    />
                                  </Box>
                                </>
                              )}
                              {showPointSizeOption && !isXAxisCurve && (
                                <Box>
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={0.5}
                                  >
                                    <Typography variant="caption">
                                      Taille des points
                                    </Typography>
                                    <Typography variant="caption">
                                      {(prefs.thicknessPx || 10) + 'px'}
                                    </Typography>
                                  </Box>
                                  <ThicknessSlider
                                    value={prefs.thicknessPx || 10}
                                    min={1}
                                    max={20}
                                    onChange={(value) =>
                                      onUpdateCurve(key, {
                                        ...prefs,
                                        thicknessPx: value,
                                      })
                                    }
                                  />
                                </Box>
                              )}
                              <Box>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  mb={0.5}
                                >
                                  Axe
                                </Typography>
                                <AxisMappingDropdown
                                  value={dataSourceToAxis[key as CurveId] || ''}
                                  axes={axes}
                                  onChange={(value) =>
                                    onUpdateAxisMapping(
                                      key as CurveId,
                                      value as AxisKey
                                    )
                                  }
                                />
                              </Box>
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Draggable>
                    {showLinkedIcon && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={0.25}
                        sx={{ color: 'text.disabled' }}
                      >
                        <Link2 size={14} />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};
